const FUNNEL_NAME = "skyward_valuation";
const DEFAULT_GA_MEASUREMENT_ID = "G-570T2K6J3V";
const SENSITIVE_KEYS = new Set([
  "email",
  "phone",
  "full_name",
  "client_name",
  "name",
  "address",
  "exact_address",
  "token",
  "captcha",
  "recaptcha",
  "follow_up_token",
]);

let gaScriptRequested = false;
let gaInitialized = false;

function isSensitiveKey(key) {
  const normalized = String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase();

  return (
    SENSITIVE_KEYS.has(normalized) ||
    normalized.endsWith("_token") ||
    normalized.includes("email") ||
    normalized.includes("phone") ||
    normalized.includes("address")
  );
}

function getGaMeasurementId() {
  const configured = String(
    process.env.REACT_APP_GA_MEASUREMENT_ID || DEFAULT_GA_MEASUREMENT_ID
  ).trim();

  return /^G-[A-Z0-9]+$/i.test(configured) ? configured : "";
}

function loadGaScript(measurementId) {
  if (gaScriptRequested || document.getElementById("skyward-ga4")) return;

  gaScriptRequested = true;
  const script = document.createElement("script");
  script.id = "skyward-ga4";
  script.async = true;
  script.src =
    "https://www.googletagmanager.com/gtag/js?id=" +
    encodeURIComponent(measurementId);
  document.head.appendChild(script);
}

function ensureGa4() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const measurementId = getGaMeasurementId();
  if (!measurementId) return false;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  loadGaScript(measurementId);

  if (!gaInitialized) {
    window.gtag("js", new Date());
    window.gtag("config", measurementId, {
      send_page_view: false,
      debug_mode: process.env.NODE_ENV !== "production",
    });
    gaInitialized = true;
  }

  return true;
}

function getFunnelSessionId() {
  try {
    const key = "skyward_funnel_session_id";
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;

    const generated = `skyward_${Date.now()}_${Math.random()
      .toString(16)
      .slice(2, 8)}`;
    window.sessionStorage.setItem(key, generated);
    return generated;
  } catch (error) {
    return "session_unavailable";
  }
}

function cleanValue(value) {
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map(cleanValue);
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((clean, [key, childValue]) => {
      if (isSensitiveKey(key)) return clean;
      clean[key] = cleanValue(childValue);
      return clean;
    }, {});
  }

  if (typeof value === "string") {
    return value.slice(0, 140);
  }

  return value;
}

function cleanEventName(eventName) {
  return String(eventName || "skyward_event")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .slice(0, 40);
}

function baseAnalyticsPayload(payload) {
  return cleanValue({
    ...payload,
    funnel_name: FUNNEL_NAME,
    funnel_session_id: getFunnelSessionId(),
    page_path: window.location.pathname,
    event_ts: new Date().toISOString(),
  });
}

export function pushAnalyticsEvent(eventName, payload = {}) {
  if (typeof window === "undefined") return;

  const cleanName = cleanEventName(eventName);
  const cleanPayload = baseAnalyticsPayload(payload);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...cleanPayload,
    event: cleanName,
  });

  if (ensureGa4() && typeof window.gtag === "function") {
    window.gtag("event", cleanName, {
      ...cleanPayload,
      event_category: "skyward_funnel",
      debug_mode: process.env.NODE_ENV !== "production",
    });
  }
}

export function trackFunnelStepView(stepNumber, stepLabel, payload = {}) {
  pushAnalyticsEvent("form_step_view", {
    ...payload,
    step_number: stepNumber,
    step_label: stepLabel,
  });
}

export function trackFunnelStepComplete(stepNumber, stepLabel, payload = {}) {
  pushAnalyticsEvent("form_step_complete", {
    ...payload,
    step_number: stepNumber,
    step_label: stepLabel,
  });
}

export function trackFunnelAction(eventName, payload = {}) {
  pushAnalyticsEvent(eventName, payload);
}

export function trackFunnelError(
  stepNumber,
  stepLabel,
  errorCode,
  payload = {}
) {
  pushAnalyticsEvent("form_step_error", {
    ...payload,
    step_number: stepNumber,
    step_label: stepLabel,
    error_code: errorCode,
  });
}
